import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

export default function GeoQueueSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geoQueueEnabled, setGeoQueueEnabled] = useState(false);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/geo-queue/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load geo-queue setting');
      if (!isMountedRef.current) return;
      setGeoQueueEnabled(data.geoQueueEnabled === true);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (nextEnabled) => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/geo-queue/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geoQueueEnabled: nextEnabled === true })
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save geo-queue setting');
      if (!isMountedRef.current) return;
      setGeoQueueEnabled(data.geoQueueEnabled === true);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      setSuccess(
        data.geoQueueEnabled
          ? 'Geo-queue separation ENABLED — players are now matched within their geographic region.'
          : 'Geo-queue separation DISABLED — all players share a single global queue.'
      );
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, userId]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Geo-Queue Separation</Typography>
          <Typography variant="body2" color="textSecondary">
            When enabled, players are separated into regional matchmaking queues (EUME, NA, SA, APAC) based on their country of residence. Each queue runs its own tick and players only see servers in their region.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Status: {geoQueueEnabled ? 'ENABLED (regional queues)' : 'DISABLED (single global queue)'}
          </Typography>

          <Button
            variant="contained"
            color={geoQueueEnabled ? 'secondary' : 'primary'}
            onClick={() => save(!geoQueueEnabled)}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : (geoQueueEnabled ? 'Disable Geo-Queues' : 'Enable Geo-Queues')}
          </Button>

          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {updatedAtMs ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Last updated: {new Date(updatedAtMs).toISOString()}
          </Typography>
        ) : null}

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}
      </Collapse>
    </Paper>
  );
}
